import { useMemo, useState } from 'react'
import styled from 'styled-components'
import { Modal } from 'Components/Modal/Modal'
import { ModalHeader } from 'Components/Modal/ModalHeader'
import { ModalBody } from 'Components/Modal/ModalBody'
import { StyledButton } from 'Components/Buttons/StyledButton'
import { ModalType, usePeoplePageContext } from 'Contexts/PeoplePageContext'
import { useEmployerInfoContext } from 'Contexts/EmployerInfoContext'
import { DataEditor } from '@glideapps/glide-data-grid'
import '@glideapps/glide-data-grid/dist/index.css'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { updateUserInformation, getUsersWithMetadata } from 'Services/users/userService'
import { toast } from 'react-toastify'
import { ButtonContainer, spacingSM } from 'Utils/styles/spacing'
import { zirconGray } from 'Utils/styles/colors'
import { QueryNames } from 'Hooks/api/queryNames'
import { useErrorReporter } from 'Hooks/useErrorReporter'
import { useGetEmployerMetaFields } from 'Hooks/api/employer/useGetEmployerMetaFields'
import { useMetadataGrid } from './hooks/useMetadataGrid'
import { useMetadataValidation } from './hooks/useMetadataValidation'
import { useGridColumns } from './hooks/useGridColumns'
import { useGridCellContent } from './hooks/useGridCellContent'
import { useGridEditing } from './hooks/useGridEditing'
import { useMetadataSubmit } from './hooks/useMetadataSubmit'

const GridContainer = styled.div`
  height: calc(80vh - 400px);
  width: 100%;
  position: relative;

  * {
    box-sizing: border-box;
  }
`

const CompactModalBody = styled(ModalBody)`
  gap: ${spacingSM};
`

const Summary = styled.div`
  padding: ${spacingSM};
  background-color: ${zirconGray};
  border-radius: 4px;
  font-size: 14px;
  text-align: center;
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
`

const ErrorText = styled.span`
  color: #dc2626;
  font-weight: 500;
`

const Description = styled.p`
  line-height: 1.5;
`

const SearchContainer = styled.div`
  display: flex;
  gap: ${spacingSM};
  margin-bottom: ${spacingSM};
  align-items: center;
`

const SearchInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${zirconGray};
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`

const ToggleContainer = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  white-space: nowrap;

  input[type='checkbox'] {
    cursor: pointer;
  }
`

export const BulkMetadataGridModal = () => {
  const queryClient = useQueryClient()
  const reportError = useErrorReporter()
  const [searchTerm, setSearchTerm] = useState('')
  const [showOnlyChanged, setShowOnlyChanged] = useState(false)

  const {
    state: { modal },
    setModal
  } = usePeoplePageContext()
  const {
    state: { employerId }
  } = useEmployerInfoContext()
  const { data: metaFieldsData } = useGetEmployerMetaFields(employerId)
  const { data, isLoading } = useQuery(
    [QueryNames.UsersWithMetadata, employerId],
    () => getUsersWithMetadata(employerId!),
    {
      enabled: modal === ModalType.BulkMetadataGrid && !!employerId,
      onError: (err: string) => {
        reportError(`Failed to load users with metadata: ${err}`)
        toast.error('Failed to load user data')
      }
    }
  )
  const { mutateAsync: updateMetadata, isLoading: isUpdating } = useMutation(
    updateUserInformation,
    {
      onSuccess: (response) => {
        if (!response.success) {
          reportError(`Failed to save changes: ${response.message}`)
          toast.error(response.message || 'Failed to save changes')
          return
        }

        toast.success('Changes saved successfully')
        queryClient.invalidateQueries([QueryNames.UsersForEmployer])
        queryClient.invalidateQueries([QueryNames.UsersWithMetadata])
        setShowModal(false)
      },
      onError: (err: string) => {
        reportError(`Failed to save changes: ${err}`)
        toast.error('Failed to save changes')
      }
    }
  )

  const users = useMemo(() => data?.users || [], [data?.users])
  const metaFields = useMemo(() => {
    if (!metaFieldsData || metaFieldsData.length === 0) return []
    return metaFieldsData.sort((a, b) => a.fieldName.localeCompare(b.fieldName))
  }, [metaFieldsData])

  const {
    changes,
    changesRef,
    validationErrorsRef,
    validationErrors,
    gridSelection,
    setGridSelection,
    hasValidationErrors,
    hasChanges,
    resetState,
    batchUpdateChanges,
    batchUpdateErrors
  } = useMetadataGrid()

  const filteredUsers = useMemo(() => {
    const searchFiltered = searchTerm.trim()
      ? users.filter((user) => {
          const term = searchTerm.toLowerCase()
          return (
            user.email.toLowerCase().includes(term) ||
            (user.first_name ?? '').toLowerCase().includes(term) ||
            (user.last_name ?? '').toLowerCase().includes(term) ||
            Object.values(user.metadata).some(
              (value) => value && value.toLowerCase().includes(term)
            )
          )
        })
      : users

    return showOnlyChanged
      ? searchFiltered.filter((user) =>
          Array.from(changes.values()).some((change) => change.userId === user.user_id)
        )
      : searchFiltered
  }, [users, searchTerm, showOnlyChanged, changes])

  const { columns, columnKinds } = useGridColumns(metaFields)
  const { validateField } = useMetadataValidation(metaFields, users)
  const { getCellContent } = useGridCellContent(
    filteredUsers,
    columns,
    columnKinds,
    changes,
    validationErrors
  )
  const { onCellEdited, onPaste } = useGridEditing(
    filteredUsers,
    columns,
    columnKinds,
    changesRef,
    validationErrorsRef,
    validateField,
    batchUpdateChanges,
    batchUpdateErrors
  )
  const { handleSubmit } = useMetadataSubmit(employerId, changes, validationErrors, updateMetadata)
  const setShowModal = (show: boolean) => {
    if (!show) {
      resetState()
      setSearchTerm('')
      setShowOnlyChanged(false)
    }
    setModal(show ? ModalType.BulkMetadataGrid : undefined)
  }
  const handlePaste = (
    target: readonly [number, number],
    values: readonly (readonly string[])[]
  ): boolean => onPaste(target, values, gridSelection)

  return (
    <Modal
      showModal={modal === ModalType.BulkMetadataGrid}
      setShowModal={setShowModal}
      minWidth={800}
      maxWidth={1400}
    >
      <ModalHeader>Bulk Edit Names &amp; Metadata</ModalHeader>
      <CompactModalBody>
        {isLoading ? (
          <LoadingContainer>Loading...</LoadingContainer>
        ) : (
          <>
            <Description>
              Edit names and metadata fields directly in the grid below. Modified cells will be
              highlighted in yellow.
              <br />
              Required fields are marked with *, unique fields with (unique). The email column is
              read-only.
            </Description>
            <SearchContainer>
              <SearchInput
                type="text"
                placeholder="Search by name, email, or metadata value..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <ToggleContainer>
                <input
                  type="checkbox"
                  checked={showOnlyChanged}
                  onChange={(e) => setShowOnlyChanged(e.target.checked)}
                />
                Show only changed
              </ToggleContainer>
            </SearchContainer>
            <GridContainer>
              <DataEditor
                columns={columns}
                rows={filteredUsers.length}
                getCellContent={getCellContent}
                onCellEdited={onCellEdited}
                onPaste={handlePaste}
                gridSelection={gridSelection}
                onGridSelectionChange={setGridSelection}
                smoothScrollX={true}
                smoothScrollY={true}
                rowMarkers="number"
                freezeColumns={3}
                getCellsForSelection={true}
                overscrollX={0}
                overscrollY={0}
              />
            </GridContainer>
          </>
        )}
        {(hasChanges || hasValidationErrors) && (
          <Summary>
            {hasChanges && (
              <>
                {changes.size} change{changes.size !== 1 ? 's' : ''}
              </>
            )}
            {hasChanges && hasValidationErrors && ' | '}
            {hasValidationErrors && (
              <ErrorText>
                {validationErrors.size} validation error{validationErrors.size !== 1 ? 's' : ''}
              </ErrorText>
            )}
          </Summary>
        )}
      </CompactModalBody>

      <ButtonContainer gap={spacingSM}>
        <StyledButton secondary onClick={() => setShowModal(false)} id="cancel.label" />
        <StyledButton
          primary
          onClick={handleSubmit}
          disabled={isLoading || isUpdating || !hasChanges || hasValidationErrors}
          isLoading={isUpdating}
          id="users.bulkMetadataGrid.submit"
        />
      </ButtonContainer>
    </Modal>
  )
}
